/* =========================================================================
   "Today is a feast day" announcement — pure gating logic, unit-tested.

   WHAT THIS IS. A full-screen interrupt at the FIRST break of a game session
   (the same seam Brother Theo's one-time introduction uses, gameController
   showBreak) telling the player which feast the Church keeps today. The content
   is the already-plumbed FeastMoment from GET /api/meta/feast-moment — no new
   backend, no new data.

   WHY IT IS GATED SO HARD. On 2026-07-21 the home screen's prominent "Today's
   Feast" stop was deliberately DEMOTED to a low-key rotating tile because most
   players — especially brand-new and guest ones — don't follow the liturgical
   calendar and the card read as noise. This announcement is NOT a reversal of
   that call: it is the targeted version of it. Instead of showing a feast card
   to everyone, it shows a genuinely noticeable one ONLY to the small subset of
   players who are signed in, have stuck around, and already know who Brother
   Theo is — i.e. exactly the people for whom "today is special" lands.

   The four gates:
     · isAuthed          — api.isAuthenticated(); guests never see it.
     · gamesCompleted    — easyStart.getGamesCompleted() >= MIN_GAMES_COMPLETED.
     · theoIntroduced    — progression.isTheoIntroduced(); the announcement is
                           framed as Theo speaking, so he must already be known.
     · alreadyShownToday — at most once per ET calendar day.

   ONCE-PER-DAY, NOT ONCE-EVER. The tracker below is intentionally NOT the
   `catholics.theoIntroduced` one-shot-forever flag: a feast day recurs, so this
   stores the last ET date it fired and must be able to fire again tomorrow.

   House pattern: pure functions over an injectable KVStore seam (mirrors
   feasts.ts / dailyBlessing.ts) so every rule here is testable without a DOM.
   ========================================================================= */
import type { KVStore } from './dailyBlessing';
import type { FeastMoment } from './api';
import { etDateString } from './today';

/** Last ET date (YYYY-MM-DD) the announcement fired. Deliberately separate from
    the Theo-intro flag — this one is per-day, not once-ever. The `catholics.`
    prefix is load-bearing (main.ts's ?fresh reset wipes by prefix). */
const KEY = 'catholics.feastAnnounced';

/** "Longer-term player" bar. THIS IS A TUNABLE JUDGMENT CALL: three completed
    games on this device. Rationale — a player who met Theo for the first time in
    game 1 should not also be interrupted by a feast announcement in that same
    session; three games is a reasonable "has stuck around" signal, and it reuses
    the existing device-local counter rather than inventing new state. */
export const MIN_GAMES_COMPLETED = 3;

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/* ------------------------------- the gates ------------------------------- */

export interface FeastAnnouncementGates {
  isAuthed: boolean;
  gamesCompleted: number;
  theoIntroduced: boolean;
  feastMoment: FeastMoment | null;
  alreadyShownToday: boolean;
}

/** PURE: the network-free half of the decision. Every gate that can be answered
    from local state alone, so the caller can skip the feast-moment fetch
    entirely for the (large) majority of players who could never qualify. */
export function feastAnnouncementEligible(
  isAuthed: boolean,
  gamesCompleted: number,
  theoIntroduced: boolean,
  alreadyShownToday: boolean,
): boolean {
  if (!isAuthed) return false;
  if (!theoIntroduced) return false;
  if (alreadyShownToday) return false;
  return gamesCompleted >= MIN_GAMES_COMPLETED;
}

/** PURE: the whole decision, including "is there actually a feast today". A null
    feastMoment is the API's valid "no feast today" answer, not an error. */
export function shouldShowFeastAnnouncement(g: FeastAnnouncementGates): boolean {
  if (!feastAnnouncementEligible(g.isAuthed, g.gamesCompleted, g.theoIntroduced, g.alreadyShownToday)) {
    return false;
  }
  return !!g.feastMoment && !!g.feastMoment.title;
}

/* --------------------------- per-day fire tracker --------------------------- */

/** Has the announcement already fired on today's ET calendar day? */
export function wasAnnouncedToday(now: Date = new Date(), store: KVStore | null = deviceStore()): boolean {
  try {
    return !!store && store.getItem(KEY) === etDateString(now);
  } catch {
    return false;
  }
}

/** Stamp today as announced. Returns true only on the FIRST stamp of the day, so
    the caller fires telemetry / shows the overlay exactly once. */
export function markFeastAnnounced(now: Date = new Date(), store: KVStore | null = deviceStore()): boolean {
  const today = etDateString(now);
  try {
    if (!store) return false;
    if (store.getItem(KEY) === today) return false;
    store.setItem(KEY, today);
    return true;
  } catch {
    return false; // storage unavailable — it simply may re-announce next session
  }
}

/* ------------------------------ presentation ------------------------------ */

/** How much visual weight the day deserves. A Solemnity (Christmas, the
    Assumption…) or a Feast outranks an optional memorial, so it gets the fuller
    treatment; everything else, including an unknown/absent rank, stays standard. */
export type FeastEmphasis = 'solemn' | 'standard';

export function feastEmphasis(rank: string | null | undefined): FeastEmphasis {
  const r = (rank || '').trim().toLowerCase();
  return r === 'solemnity' || r === 'feast' ? 'solemn' : 'standard';
}

/** The liturgical color of the day, normalized to the five accents the overlay
    can theme. Unknown / missing → 'gold', the app's house accent. */
export type FeastAccent = 'gold' | 'red' | 'green' | 'violet' | 'rose';

export function feastAccent(color: string | null | undefined): FeastAccent {
  const c = (color || '').trim().toLowerCase();
  if (c === 'red') return 'red';
  if (c === 'green') return 'green';
  if (c === 'violet' || c === 'purple') return 'violet';
  if (c === 'rose' || c === 'pink') return 'rose';
  return 'gold'; // white, gold, or anything unrecognized
}

/** Auto-continue delay, mirroring Did You Know's tap-or-wait mechanic. A solemn
    day carries a longer story card, so it holds a little longer. */
export function feastAutoDismissMs(emphasis: FeastEmphasis): number {
  return emphasis === 'solemn' ? 15_000 : 12_000;
}

/** The announcement's body copy. Uses the feast's own storyText when the API
    supplied one, otherwise a title-driven line — never feast-specific hardcoding,
    so it reads correctly for whatever the calendar serves. */
export function feastAnnouncementBody(m: FeastMoment): string {
  const story = (m.storyText || '').trim();
  return story || `Today the Church honors ${m.title}.`;
}
